import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Heart, Users, Sparkles, CreditCard, Send, CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { getInvolvedOptions } from '../data/mock';
import { submitVolunteerApplication } from '../services/api';

const GetInvolvedPage = () => {
  const { subpage } = useParams();
  const [volunteerForm, setVolunteerForm] = useState({
    name: '',
    email: '',
    phone: '',
    availability: '',
    interests: '',
    experience: '',
    consent: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleVolunteerSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await submitVolunteerApplication(volunteerForm);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Donate subpage
  if (subpage === 'donate') {
    return (
      <main>
        <div className="page-header">
          <div className="container">
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <ChevronRight size={16} />
              <Link to="/get-involved">Get Involved</Link>
              <ChevronRight size={16} />
              <span>Donate</span>
            </div>
            <h1 className="display-medium">Make a Donation</h1>
            <p className="body-large">
              Your generosity directly supports horses in need across Scotland.
            </p>
          </div>
        </div>

        <section className="section">
          <div className="container">
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
              <div className="network-card" style={{ padding: '48px', textAlign: 'center' }}>
                <Heart size={48} color="var(--brand-dark)" style={{ marginBottom: '24px' }} />
                <h2 className="heading-2" style={{ marginBottom: '16px' }}>Thank You for Your Support</h2>
                <p className="body-medium" style={{ marginBottom: '32px' }}>
                  Every donation helps us provide food, shelter, veterinary care, and love to horses who have nowhere else to turn. Your kindness makes a real difference.
                </p>

                <div className="grid-3" style={{ marginBottom: '40px' }}>
                  {[
                    { amount: '£10', impact: 'Provides a week of feed for one horse' },
                    { amount: '£25', impact: 'Covers a farrier visit for one horse' },
                    { amount: '£50', impact: 'Funds emergency veterinary care' }
                  ].map((tier, index) => (
                    <div 
                      key={index}
                      style={{ 
                        padding: '24px', 
                        background: 'var(--bg-subtle)', 
                        borderRadius: '16px',
                        textAlign: 'center'
                      }}
                    >
                      <p className="display-small" style={{ marginBottom: '8px' }}>{tier.amount}</p>
                      <p className="body-small">{tier.impact}</p>
                    </div>
                  ))}
                </div>

                <div 
                  style={{ 
                    background: '#FEF3C7', 
                    padding: '24px', 
                    borderRadius: '16px',
                    marginBottom: '32px'
                  }}
                >
                  <CreditCard size={24} color="#92400E" style={{ marginBottom: '12px' }} />
                  <p className="body-medium" style={{ color: '#92400E' }}>
                    <strong>Donation Integration Coming Soon</strong><br />
                    We are currently setting up secure online donations. In the meantime, please contact us to arrange a donation via bank transfer or cheque.
                  </p>
                </div>

                <Link to="/contact" className="btn-primary">
                  Contact Us to Donate
                  <ArrowRight size={18} style={{ marginLeft: '8px' }} />
                </Link>
              </div>

              <div className="network-card" style={{ marginTop: '32px' }}>
                <h3 className="heading-3" style={{ marginBottom: '16px' }}>Other Ways to Give</h3>
                <ul style={{ paddingLeft: '20px' }}>
                  <li className="body-medium" style={{ marginBottom: '12px' }}>
                    <strong>Regular Giving:</strong> Set up a standing order for consistent support
                  </li>
                  <li className="body-medium" style={{ marginBottom: '12px' }}>
                    <strong>In Memory:</strong> Make a donation in memory of a loved one or beloved horse
                  </li>
                  <li className="body-medium" style={{ marginBottom: '12px' }}>
                    <strong>Legacy Gifts:</strong> Remember The Grazing Trust in your will
                  </li>
                  <li className="body-medium">
                    <strong>Corporate Giving:</strong> Partner with us as a business
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }

  // Volunteer subpage
  if (subpage === 'volunteer') {
    if (submitted) {
      return (
        <main>
          <div className="page-header">
            <div className="container">
              <h1 className="display-medium">Thank You!</h1>
            </div>
          </div>
          <section className="section">
            <div className="container" style={{ maxWidth: '600px' }}>
              <div className="success-message" style={{ padding: '48px', textAlign: 'center' }}>
                <CheckCircle2 size={48} color="#10b981" style={{ marginBottom: '16px' }} />
                <h2 className="heading-2" style={{ marginBottom: '16px' }}>Application Received</h2>
                <p className="body-medium" style={{ marginBottom: '24px' }}>
                  Thank you for your interest in volunteering. We&apos;ll be in touch soon to discuss opportunities.
                </p>
                <Link to="/" className="btn-primary">Return to Home</Link>
              </div>
            </div>
          </section>
        </main>
      );
    }

    return (
      <main>
        <div className="page-header">
          <div className="container">
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <ChevronRight size={16} />
              <Link to="/get-involved">Get Involved</Link>
              <ChevronRight size={16} />
              <span>Volunteer</span>
            </div>
            <h1 className="display-medium">Become a Volunteer</h1>
            <p className="body-large">
              Join our team of dedicated volunteers making a difference for horses.
            </p>
          </div>
        </div>

        <section className="section">
          <div className="container">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '64px' }}>
              <div>
                <h2 className="heading-2" style={{ marginBottom: '24px' }}>Why Volunteer?</h2>
                <p className="body-medium" style={{ marginBottom: '24px' }}>
                  Our volunteers are the heart of The Grazing Trust. Whether you can spare a few hours or want to take on a regular role, your help makes a real difference.
                </p>

                <h3 className="heading-3" style={{ marginBottom: '16px' }}>Volunteer Roles Include:</h3>
                <ul style={{ paddingLeft: '20px', marginBottom: '24px' }}>
                  <li className="body-medium" style={{ marginBottom: '8px' }}>Yard duties and horse care</li>
                  <li className="body-medium" style={{ marginBottom: '8px' }}>Grooming and handling</li>
                  <li className="body-medium" style={{ marginBottom: '8px' }}>Fundraising and events</li>
                  <li className="body-medium" style={{ marginBottom: '8px' }}>Administrative support</li>
                  <li className="body-medium" style={{ marginBottom: '8px' }}>Social media and marketing</li>
                  <li className="body-medium">Professional skills (vet, farrier, trainer)</li>
                </ul>

                <div 
                  style={{ 
                    background: 'var(--bg-subtle)', 
                    padding: '20px', 
                    borderRadius: '12px' 
                  }}
                >
                  <p className="body-small">
                    <strong>Note:</strong> All volunteers working directly with horses will need to complete an induction and may require a PVG check.
                  </p>
                </div>
              </div>

              <div className="network-card" style={{ padding: '40px' }}>
                <h2 className="heading-2" style={{ marginBottom: '8px' }}>Volunteer Interest Form</h2>
                <p className="body-small" style={{ marginBottom: '32px' }}>
                  Tell us about yourself and how you&apos;d like to help.
                </p>

                {error && (
                  <div 
                    style={{ 
                      background: '#FEE2E2', 
                      padding: '16px', 
                      borderRadius: '12px', 
                      marginBottom: '24px',
                      color: '#DC2626'
                    }}
                  >
                    {error}
                  </div>
                )}

                <form onSubmit={handleVolunteerSubmit}>
                  <div className="form-group">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input 
                      id="name"
                      value={volunteerForm.name}
                      onChange={(e) => setVolunteerForm(prev => ({ ...prev, name: e.target.value }))}
                      required
                      style={{ marginTop: '8px' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div className="form-group">
                      <Label htmlFor="email">Email *</Label>
                      <Input 
                        id="email"
                        type="email"
                        value={volunteerForm.email}
                        onChange={(e) => setVolunteerForm(prev => ({ ...prev, email: e.target.value }))}
                        required
                        style={{ marginTop: '8px' }}
                      />
                    </div>
                    <div className="form-group">
                      <Label htmlFor="phone">Phone</Label>
                      <Input 
                        id="phone"
                        type="tel"
                        value={volunteerForm.phone}
                        onChange={(e) => setVolunteerForm(prev => ({ ...prev, phone: e.target.value }))}
                        style={{ marginTop: '8px' }}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <Label>Availability</Label>
                    <Select 
                      value={volunteerForm.availability}
                      onValueChange={(value) => setVolunteerForm(prev => ({ ...prev, availability: value }))}
                    >
                      <SelectTrigger style={{ marginTop: '8px' }}>
                        <SelectValue placeholder="How often can you help?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="fortnightly">Fortnightly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="events">Events only</SelectItem>
                        <SelectItem value="flexible">Flexible</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="form-group">
                    <Label htmlFor="interests">Areas of Interest</Label>
                    <Textarea 
                      id="interests"
                      value={volunteerForm.interests}
                      onChange={(e) => setVolunteerForm(prev => ({ ...prev, interests: e.target.value }))}
                      placeholder="What roles would you be interested in?"
                      style={{ marginTop: '8px' }}
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="experience">Relevant Experience</Label>
                    <Textarea 
                      id="experience"
                      value={volunteerForm.experience}
                      onChange={(e) => setVolunteerForm(prev => ({ ...prev, experience: e.target.value }))}
                      placeholder="Tell us about any experience with horses or relevant skills..."
                      style={{ marginTop: '8px' }}
                    />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '24px' }}>
                    <Checkbox 
                      id="consent"
                      checked={volunteerForm.consent}
                      onCheckedChange={(checked) => setVolunteerForm(prev => ({ ...prev, consent: checked }))}
                    />
                    <Label htmlFor="consent" style={{ fontWeight: 400, fontSize: '14px' }}>
                      I consent to The Grazing Trust storing my details and contacting me about volunteering opportunities. *
                    </Label>
                  </div>

                  <button 
                    type="submit" 
                    className="btn-primary" 
                    style={{ width: '100%' }}
                    disabled={!volunteerForm.consent || loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 size={18} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={18} style={{ marginRight: '8px' }} />
                        Submit Application
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </main>
    );
  }

  // Fundraise subpage
  if (subpage === 'fundraise') {
    return (
      <main>
        <div className="page-header">
          <div className="container">
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <ChevronRight size={16} />
              <Link to="/get-involved">Get Involved</Link>
              <ChevronRight size={16} />
              <span>Fundraise</span>
            </div>
            <h1 className="display-medium">Fundraise for Us</h1>
            <p className="body-large">
              Organise your own event or challenge to help horses in need.
            </p>
          </div>
        </div>

        <section className="section">
          <div className="container" style={{ maxWidth: '800px' }}>
            <div className="network-card" style={{ padding: '48px', textAlign: 'center', marginBottom: '32px' }}>
              <Sparkles size={48} color="var(--brand-dark)" style={{ marginBottom: '24px' }} />
              <h2 className="heading-2" style={{ marginBottom: '16px' }}>Your Fundraiser, Your Way</h2>
              <p className="body-medium" style={{ marginBottom: '32px' }}>
                From sponsored walks to bake sales, there are endless ways to raise funds for The Grazing Trust. We&apos;ll provide support and materials to help make your fundraiser a success.
              </p>
            </div>

            <h3 className="heading-2" style={{ marginBottom: '24px' }}>Fundraising Ideas</h3>
            <div className="grid-2" style={{ marginBottom: '48px' }}>
              {[
                { title: 'Sponsored Rides', desc: 'Get sponsored per mile or for completing a challenge route' },
                { title: 'Bake Sales', desc: 'Horse-themed cupcakes always go down well!' },
                { title: 'Quiz Nights', desc: 'Test your friends\' equine knowledge' },
                { title: 'Birthday Donations', desc: 'Ask for donations instead of gifts' },
                { title: 'Corporate Events', desc: 'Workplace challenges and team building' },
                { title: 'Online Campaigns', desc: 'Set up a crowdfunding page' }
              ].map((idea, index) => (
                <div key={index} className="network-card">
                  <h4 className="heading-3" style={{ marginBottom: '8px' }}>{idea.title}</h4>
                  <p className="body-medium">{idea.desc}</p>
                </div>
              ))}
            </div>

            <div style={{ textAlign: 'center' }}>
              <p className="body-medium" style={{ marginBottom: '24px' }}>
                Want to start a fundraiser? Get in touch and we&apos;ll help you get started.
              </p>
              <Link to="/contact" className="btn-primary">
                Contact Us
                <ArrowRight size={18} style={{ marginLeft: '8px' }} />
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  // Main Get Involved page
  return (
    <main>
      <div className="page-header">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <ChevronRight size={16} />
            <span>Get Involved</span>
          </div>
          <h1 className="display-medium">Get Involved</h1>
          <p className="body-large">
            There are many ways you can support our mission to help horses in need.
          </p>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="grid-3">
            {getInvolvedOptions.map((option) => {
              const icons = { Heart, Users, Sparkles };
              const Icon = icons[option.icon] || Heart;
              return (
                <Link 
                  key={option.id}
                  to={option.link}
                  className="network-card hover-lift"
                  style={{ textDecoration: 'none', textAlign: 'center' }}
                >
                  <div 
                    style={{ 
                      width: '80px', 
                      height: '80px', 
                      background: 'var(--brand-primary)', 
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 24px'
                    }}
                  >
                    <Icon size={36} color="var(--brand-dark)" />
                  </div>
                  <h2 className="heading-2" style={{ marginBottom: '16px' }}>{option.title}</h2>
                  <p className="body-medium" style={{ marginBottom: '24px' }}>{option.description}</p>
                  <span className="btn-primary">{option.cta}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2 className="display-small">Every Contribution Matters</h2>
          <p className="body-large" style={{ maxWidth: '600px', margin: '0 auto 32px' }}>
            Whether you give time, money, or simply share our mission, you&apos;re helping horses find safety and hope.
          </p>
          <Link to="/contact" className="btn-cta">Get in Touch</Link>
        </div>
      </section>
    </main>
  );
};

export default GetInvolvedPage;
